// The Requisitions list on Mission Control (`#requisitions`, D-11, lane B2-FU; RQ-1…RQ-5, RQ-7): the requisitions this
// seat may list (`requisition.list`; never filtered by role here), drawn from D0 parts in three views — the table, the
// Cards (DC-53: a requisition card carries health and readiness, not completion; the default at ≤ 768) and the board,
// whose columns are DC-30's seven stations (boardColumn.ts `stationOf`, the station the record's own track draws
// active). His words throughout: the status from REQUISITION_STATUS, the level from LEVEL_DISPLAY, the Recruiter, Last
// contact, the Phase as the §4.3 station, Days open · Active Time · Days on Hold; V1's phase-target column and phase
// filter are not drawn (DC-30: the Process Guide phase clocks are drawn nowhere). Other lanes mount through
// views/requisition/slots.ts (C-B10-13): the list's columns (B10's Req health), its one batched signals read and its
// saved views. At ≤ 480 the table's rows are cards (each cell carries its column's words).
// Registered for the bare route and its query string from views/b2/register.ts, so the registry wins over V1's
// Requisitions.tsx, which stays the screen of the two lists this one does not re-host yet: the Interviewer's own list
// (the panelist projection) and the archive search, both drawn through the shell's V1 slot.
// Hosted by MissionShell inside its `.mc` island, so it draws no McRoot of its own.

import { useEffect, useId, useMemo, useRef, useState, useSyncExternalStore, type KeyboardEvent } from "react";
import { recruiterQueue, requisitionList, type OperationalListRow, type PanelistListRow, type RequisitionListInput, type RequisitionListListRow } from "@/api/tools";
import { go } from "@/router";
import { useSession } from "@/session/useSession";
import { V1Slot } from "@/shell/island";
import type { ViewProps } from "@/shell/viewRegistry";
import { useViewState } from "@/shell/viewState";
import {
  ACCESS_LABELS, ACTION_LABELS, Button, Card, CheckTile, DEFAULT_TIME_ZONE, DOCUMENT_LABELS, ErrorStrip, FILTERBAR_LABELS, FilterBar, Flex, G1_LABELS, G6_CAPABILITY_LABELS, G7_TEMPLATES, Grid,
  Count, IconButton, Input, Kanban, KeyValue, LEVEL_DISPLAY, MASKED_LABELS, METRIC_LABELS, MILESTONE_LABELS, NAV_ITEM_LABELS, PageHeader, Pill,
  RECRUITER_ASSIGNMENT_STATUS, REQUISITION_STATUS, ROLE_LABELS,
  RULE_LABELS, SLA_LABELS, SURFACE_LABELS, SectionTitle, Segmented, Select, Skeleton, SlideOver, Stack, Table, Td, TextLink, Th, Tr, WIDGET_LABELS, formatDate,
  toEpoch, useMc, type FilterBarFilter, type KeyValueItem, type Level,
} from "@/mc";
import { BRAND_LABEL } from "@/views/Requests";
import { RequisitionsList as V1RequisitionsList } from "@/views/Requisitions";
import { withQuery } from "@/views/shared/settledRead";
import { STATIONS, stationOf } from "./boardColumn";
import { useSettledRead } from "./data";
import { REQ_LABELS, REQ_LIST_LABELS as L } from "./labels";
import {
  listSignals, requisitionColumns, savedViews, type ListSignals, type RequisitionCellContext, type RequisitionColumnDef, type RequisitionListRow,
} from "./slots";

const PAGE_SIZE = 25;
const DAY = 86_400_000;
const BASE = "#requisitions";
/** The TA Manager's assignment queue (views/b2/register.ts; its page is titled Assign/reassign Recruiter). */
const ASSIGNMENT_QUEUE = "#requisitions/assignment";

/** A row the seat may read in full (or masked); the panelist rows are the Interviewer's projection. */
type FullRow = OperationalListRow | RequisitionListListRow;
const isFull = (row: RequisitionListRow): row is FullRow => "stage" in row;
const isPanelist = (row: RequisitionListRow): row is PanelistListRow => !("stage" in row);

/** The server's requisition status → his status word (G §5; V1's `draft` is not yet posted). CR-034: the acceptance of
 * the last opening is `accepted` (Offer Accepted, a milestone, not Filled) and `filled` is the HRIS hire (Filled). */
export const STATUS_WORD: Readonly<Record<string, string>> = {
  draft: REQUISITION_STATUS.created,
  open: REQUISITION_STATUS.posted,
  partially_filled: REQUISITION_STATUS.posted,
  on_hold: REQUISITION_STATUS.onHold,
  accepted: REQUISITION_STATUS.offerAccepted,
  filled: REQUISITION_STATUS.filled,
  closed_filled: REQUISITION_STATUS.filled,
  closed_cancelled: REQUISITION_STATUS.cancelled,
  archived: REQUISITION_STATUS.archived,
};

/** The live statuses (V1's LIVE): the rows the header counts as open. */
export const LIVE_STATES: readonly string[] = ["draft", "open", "on_hold", "partially_filled"];

/** The statuses in the order a list address writes them: the live ones first. */
const STATE_ORDER: readonly string[] = [...LIVE_STATES, "accepted", "filled", "closed_filled", "closed_cancelled", "archived"];

/** The status filter's boxes in his chain order: his word and the statuses it stands for (F-37). */
export const STATE_BOXES = [
  { key: "created", word: REQUISITION_STATUS.created, states: ["draft"] },
  { key: "posted", word: REQUISITION_STATUS.posted, states: ["open", "partially_filled"] },
  { key: "on-hold", word: REQUISITION_STATUS.onHold, states: ["on_hold"] },
  { key: "offer-accepted", word: REQUISITION_STATUS.offerAccepted, states: ["accepted"] },
  { key: "filled", word: REQUISITION_STATUS.filled, states: ["filled", "closed_filled"] },
  { key: "cancelled", word: REQUISITION_STATUS.cancelled, states: ["closed_cancelled"] },
  { key: "archived", word: REQUISITION_STATUS.archived, states: ["archived"] },
] as const;
export type StateBoxKey = (typeof STATE_BOXES)[number]["key"];

/** The statuses a list address lists: its `state`, else the live ones (requisition.list's own default). */
export function statesOf(params: URLSearchParams): string[] {
  const states = (params.get("state") ?? "").split(",").filter(Boolean);
  return states.length > 0 ? states : [...LIVE_STATES];
}

/** The statuses after ticking or clearing one box: built from the boxes as they are shown, never from the address alone. */
export function tickState(params: URLSearchParams, key: StateBoxKey, checked: boolean): string[] {
  const states = new Set(statesOf(params));
  for (const state of STATE_BOXES.find((box) => box.key === key)?.states ?? []) {
    if (checked) states.add(state);
    else states.delete(state);
  }
  return [...STATE_ORDER.filter((state) => states.has(state)), ...[...states].filter((state) => !STATE_ORDER.includes(state))];
}

const ticked = (params: URLSearchParams) => {
  const states = statesOf(params);
  return STATE_BOXES.filter((box) => box.states.some((state) => states.includes(state)));
};

export type ListView = "table" | "cards" | "board";

const VIEWS = [
  { value: "table", label: G1_LABELS.table, hs: "req.list.view.table" },
  { value: "cards", label: L.cards, hs: "req.list.view.cards" },
  { value: "board", label: G1_LABELS.board, hs: "req.list.view.board" },
] as const;

/** The view a list address asks for: its `view`, else the Cards at ≤ 768 (DC-53) and the table above. */
export function viewOf(params: URLSearchParams, compact: boolean): ListView {
  const view = params.get("view");
  return view === "table" || view === "cards" || view === "board" ? view : compact ? "cards" : "table";
}

/** requisition.list's input for a list address. The V1 phase filter is gone (DC-30); its key is not read. */
export function listInput(params: URLSearchParams): RequisitionListInput {
  const sort = params.get("sort");
  const input: RequisitionListInput = {
    page: Math.max(1, Number(params.get("page") ?? 1) || 1),
    page_size: PAGE_SIZE,
    sort: sort === "activity" || sort === "id" ? sort : "elapsed",
    owner: params.get("owner") === "me" ? "mine" : "anyone",
  };
  const status = (params.get("state") ?? "").split(",").filter(Boolean);
  if (status.length > 0) input.status = status;
  for (const key of ["level", "department", "brand", "q"] as const) {
    const value = params.get(key);
    if (value) input[key] = value;
  }
  return input;
}

/** True while the window is at most `px` wide (DC-38's breakpoints); false where there is no window to ask. */
function useWidthAtMost(px: number): boolean {
  const query = `(max-width: ${px}px)`;
  const read = () => typeof window !== "undefined" && typeof window.matchMedia === "function" && window.matchMedia(query).matches;
  return useSyncExternalStore(
    (notify) => {
      if (typeof window === "undefined" || typeof window.matchMedia !== "function") return () => undefined;
      const media = window.matchMedia(query);
      media.addEventListener("change", notify);
      return () => media.removeEventListener("change", notify);
    },
    read,
    read,
  );
}

const NO_SIGNALS: ListSignals = { signals: new Map(), allowed: false, loading: false };
/** No lane has registered the list's signals read: every cell is told there is none. */
function useNoSignals(): ListSignals {
  return NO_SIGNALS;
}

/** The bare `#requisitions` route, with its query string. */
export function RequisitionsList({ route }: ViewProps) {
  const { me, can } = useSession();
  // V1's own rule for its Interviewer list (every role the seat holds is Interviewer: the panelist projection) and its
  // archive search; both stay V1's screens, unchanged, until a lane re-hosts them.
  const interviewerOnly = !!me && me.roles.length > 0 && me.roles.every((role) => role === "interviewer");
  const archiveSearch = route.params.get("state") === "archived" && can("admin.archive_search");
  if (interviewerOnly || archiveSearch) return <V1Slot><V1RequisitionsList /></V1Slot>;
  return <ListBody query={route.params.toString()} />;
}

function ListBody({ query }: { query: string }) {
  const { me, can } = useSession();
  const { register } = useViewState();
  const { now } = useMc();
  const timeZone = me?.timezone || DEFAULT_TIME_ZONE;
  const compact = useWidthAtMost(768);
  const params = useMemo(() => new URLSearchParams(query), [query]);
  const view = viewOf(params, compact);
  const input = useMemo(() => listInput(params), [params]);
  const list = useSettledRead(["requisitions", input], () => requisitionList(input));
  const items = list.data?.items ?? [];
  const rows = items.filter(isFull);
  const assigned = items.filter(isPanelist);
  const useSignals = listSignals.list()[0]?.useSignals ?? useNoSignals;
  const signals = useSignals(rows.map((row) => row.id));
  const columns = requisitionColumns.list().filter((column) => column.visible?.(can) ?? true);
  const SavedViews = savedViews.list()[0]?.component;
  useEffect(() => register({ view: "requisitions", ids: {}, summary: { loading: list.isLoading, count: rows.length } }), [register, list.isLoading, rows.length]);

  /** The list at the same address with some flags replaced; a filter change goes back to the first page. */
  const set = (flags: Readonly<Record<string, string | null>>) => go(withQuery(BASE, { page: null, ...flags }, params));
  const setView = (next: ListView) => go(withQuery(BASE, { view: next === viewOf(new URLSearchParams(), compact) ? null : next }, params));
  const cell = (row: FullRow, links = true): RequisitionCellContext => ({ signal: signals.signals.get(row.id), loading: signals.loading, links });
  const shared: RowShared = { columns, cell, timeZone, now, assign: can("recruiter.queue") };
  // F-36: the rows shown that are open, never a figure the rows do not show; nothing until the list has loaded
  // F-39: a masked row shows no status, so it is never counted open (the masking policy itself is requisition.py's)
  const open = rows.filter((row) => !row.masked && LIVE_STATES.includes(row.status)).length;

  const pages = list.data ? Math.max(1, Math.ceil(list.data.total / PAGE_SIZE)) : 1;
  const page = list.data?.page ?? 1;

  return (
    <Stack gap={16}>
      <PageHeader
        title={NAV_ITEM_LABELS.requisitions}
        meta={list.data ? [<>{METRIC_LABELS.openRequisitions} <Count n={open} /></>] : undefined}
        actions={(
          <>
            {SavedViews ? <SavedViews query={params} apply={(next) => go(next ? `${BASE}?${next}` : BASE)} /> : null}
            <Segmented label={NAV_ITEM_LABELS.requisitions} options={VIEWS} value={view} onChange={setView} />
            {/* F-29, F-147 (DC-232): the queue it opens, by that page's own title, with its count, for the seat that may open it */}
            {can("recruiter.queue") ? <QueueButton /> : null}
          </>
        )}
      />
      <Filters params={params} set={set} view={view} />
      {list.error ? (
        <ErrorStrip
          message={RULE_LABELS.failedSyncRetryable}
          problem={{ status: list.error.status, refusal_code: list.error.code, request_id: list.error.requestId }}
          onRetry={() => void list.refetch()}
          
        />
      ) : null}
      {view === "table" ? <ListTable rows={rows} shared={shared} loading={list.isPending} settled={!!list.data} /> : null}
      {view === "cards" ? <ListCards rows={rows} shared={shared} loading={list.isPending} /> : null}
      {view === "board" ? <ListBoard rows={rows} shared={shared} loading={list.isPending} /> : null}
      {assigned.length > 0 ? <AssignedElsewhere rows={assigned} /> : null}
      {pages > 1 ? (
        <Flex gap={8} justify="end">
          <div role="group" aria-label={DOCUMENT_LABELS.pageStepper}>
            <Flex gap={8}>
              <IconButton sm icon="back" label={G7_TEMPLATES.pageOf({ n: page - 1, total: pages })} can={page > 1} onClick={() => go(withQuery(BASE, { page: String(page - 1) }, params))}  />
              <span aria-live="polite">{G7_TEMPLATES.pageOf({ n: page, total: pages })}</span>
              <IconButton sm icon="fwd" label={G7_TEMPLATES.pageOf({ n: page + 1, total: pages })} can={page < pages} onClick={() => go(withQuery(BASE, { page: String(page + 1) }, params))}  />
            </Flex>
          </div>
        </Flex>
      ) : null}
    </Stack>
  );
}

/**
 * F-147 (DC-232): the top-right button *Assign/reassign Recruiter* with the count of the rows `recruiter.queue` returns
 * (the same read the queue page draws, so the badge equals what it lists). A button on the list, never a nav item or a
 * text link; no count at 0; a skeleton count while the read is out; no count when it failed. Its accessible name is
 * *Assign/reassign Recruiter · n*.
 */
export function queueCount(data: { awaiting?: readonly unknown[]; reassignments?: readonly unknown[] } | undefined): number | null {
  return data ? (data.awaiting?.length ?? 0) + (data.reassignments?.length ?? 0) : null;
}

function QueueButton() {
  const queue = useSettledRead(["recruiter-queue"], () => recruiterQueue({}));
  const n = queueCount(queue.data);
  const word = G6_CAPABILITY_LABELS.assignRecruiter;
  return (
    <Button onClick={() => go(ASSIGNMENT_QUEUE)} aria-label={n ? `${word} · ${n}` : undefined} >
      {word}
      {queue.isPending ? <Skeleton width={18} /> : n ? <Count n={n} /> : null}
    </Button>
  );
}

// ---- the filters ----------------------------------------------------------------------------------------------------

const LEVEL_OPTIONS = (Object.keys(LEVEL_DISPLAY) as Level[]).map((value) => ({ value, label: LEVEL_DISPLAY[value] }));
// The brands by their names; the corporate value's words are V1's and wait for F-16 (4d), so it is not offered here.
const BRAND_OPTIONS = Object.entries(BRAND_LABEL).filter(([value]) => value !== "corporate").map(([value, label]) => ({ value, label }));
const SORT_OPTIONS = [
  { value: "elapsed", label: SLA_LABELS.activeTime },
  { value: "activity", label: L.lastContact },
  { value: "id", label: REQ_LABELS.requisition },
];

/** A text filter applied on Enter or when it loses focus. */
function useTextFilter(params: URLSearchParams, key: string, set: (flags: Record<string, string | null>) => void) {
  const [value, setValue] = useState(params.get(key) ?? "");
  useEffect(() => setValue(params.get(key) ?? ""), [params, key]);
  const apply = () => { if (value.trim() !== (params.get(key) ?? "")) set({ [key]: value.trim() || null }); };
  const onKeyDown = (event: KeyboardEvent<HTMLElement>) => { if (event.key === "Enter") apply(); };
  return { value, setValue, apply, onKeyDown };
}

export interface StatusFilterProps {
  params: URLSearchParams;
  set: (flags: Record<string, string | null>) => void;
  /** The list's view now, and the view the popover was opened in (null: closed): a view change closes it. */
  view: ListView;
  openIn: ListView | null;
  setOpenIn: (view: ListView | null) => void;
}

/** F-37: the status boxes in a popover under their button (DS #73's surface), ticked from the address or the live
 * states; each tick writes the list the boxes now show. It closes on a press outside, on Escape and on a view change. */
export function StatusFilter({ params, set, view, openIn, setOpenIn }: StatusFilterProps) {
  const id = useId();
  const anchor = useRef<HTMLSpanElement | null>(null);
  const button = useRef<HTMLButtonElement | null>(null);
  const open = openIn === view;
  useEffect(() => {
    if (!open) return undefined;
    const press = (event: PointerEvent) => { if (!anchor.current?.contains(event.target as Node)) setOpenIn(null); };
    const key = (event: globalThis.KeyboardEvent) => { if (event.key === "Escape") { setOpenIn(null); button.current?.focus(); } };
    document.addEventListener("pointerdown", press);
    document.addEventListener("keydown", key);
    return () => { document.removeEventListener("pointerdown", press); document.removeEventListener("keydown", key); };
  }, [open, setOpenIn]);
  return (
    <span className="mc-menu-anchor" ref={anchor}>
      <Button
        ref={button}
        sm
        after="down"
        aria-expanded={open}
        aria-controls={open ? id : undefined}
        onClick={() => setOpenIn(open ? null : view)}
        
      >
        {L.status}
      </Button>
      {open ? <StatusBoxes id={id} className="mc-menu" params={params} set={set} /> : null}
    </span>
  );
}

/** The status boxes themselves: in the popover, or stacked in the phone's Filters sheet. */
function StatusBoxes({ id, className, params, set }: { id?: string; className?: string; params: URLSearchParams; set: StatusFilterProps["set"] }) {
  const on = ticked(params);
  const write = (key: StateBoxKey, checked: boolean) => {
    const next = tickState(params, key, checked);
    const live = next.length === LIVE_STATES.length && LIVE_STATES.every((state) => next.includes(state));
    set({ state: next.length === 0 || live ? null : next.join(",") });
  };
  return (
    <div id={id} className={className} role="group" aria-label={L.status}>
      {STATE_BOXES.map((box) => (
        <CheckTile key={box.key} plain checked={on.includes(box)} onChange={(checked) => write(box.key, checked)} >
          {box.word}
        </CheckTile>
      ))}
    </div>
  );
}

function Filters({ params, set, view }: { params: URLSearchParams; set: (flags: Record<string, string | null>) => void; view: ListView }) {
  const [openIn, setOpenIn] = useState<ListView | null>(null);
  // F-79: on a phone every control sits behind one Filters button, in a sheet; the active filters stay as chips
  const phone = useWidthAtMost(480);
  const [sheet, setSheet] = useState(false);
  const search = useTextFilter(params, "q", set);
  const department = useTextFilter(params, "department", set);
  const level = params.get("level") ?? "";
  const owner = params.get("owner") ?? "";
  const brand = params.get("brand") ?? "";
  const chips: FilterBarFilter[] = [
    ...(params.get("state") ? [{ name: L.status, value: ticked(params).map((box) => box.word).join(", "), onRemove: () => set({ state: null }), hs: "req.list.chip.status" }] : []),
    ...(level ? [{ name: L.level, value: LEVEL_DISPLAY[level as Level] ?? level, onRemove: () => set({ level: null }), hs: "req.list.chip.level" }] : []),
    ...(owner ? [{ name: ROLE_LABELS.recruiter, value: SURFACE_LABELS.ownReqs, onRemove: () => set({ owner: null }), hs: "req.list.chip.owner" }] : []),
    ...(params.get("department") ? [{ name: REQ_LABELS.department, value: params.get("department") ?? "", onRemove: () => set({ department: null }), hs: "req.list.chip.department" }] : []),
    ...(brand ? [{ name: L.brand, value: BRAND_LABEL[brand] ?? brand, onRemove: () => set({ brand: null }), hs: "req.list.chip.brand" }] : []),
    ...(params.get("q") ? [{ name: FILTERBAR_LABELS.search, value: params.get("q") ?? "", onRemove: () => set({ q: null }), hs: "req.list.chip.search" }] : []),
  ];
  const sort = { options: SORT_OPTIONS, value: params.get("sort") ?? "elapsed", onChange: (value: string) => set({ sort: value === "elapsed" ? null : value }), hs: "req.list.sort" };
  if (phone) {
    return (
      <>
        <Flex gap={8} wrap>
          <Button sm aria-haspopup="dialog" aria-expanded={sheet} onClick={() => setSheet(true)} >
            {L.filters}{chips.length > 0 ? <>{" "}<Count n={chips.length} /></> : null}
          </Button>
          {chips.length > 0 ? <FilterBar label={NAV_ITEM_LABELS.requisitions} filters={chips} /> : null}
        </Flex>
        <SlideOver open={sheet} title={L.filters} onClose={() => setSheet(false)} >
          <Stack gap={12}>
            <Input
              sm type="search" icon="search" aria-label={FILTERBAR_LABELS.search} placeholder={FILTERBAR_LABELS.search} value={search.value}
              onChange={(event) => search.setValue(event.currentTarget.value)} onKeyDown={search.onKeyDown} onBlur={search.apply} 
            />
            <StatusBoxes params={params} set={set} />
            <Select sm aria-label={L.level} placeholder={L.level} value={level} options={LEVEL_OPTIONS} onChange={(value) => set({ level: value || null })}  />
            <Select
              sm aria-label={ROLE_LABELS.recruiter} placeholder={ROLE_LABELS.recruiter} value={owner}
              options={[{ value: "me", label: SURFACE_LABELS.ownReqs }]} onChange={(value) => set({ owner: value || null })} 
            />
            <Input
              sm aria-label={REQ_LABELS.department} placeholder={REQ_LABELS.department} value={department.value}
              onChange={(event) => department.setValue(event.currentTarget.value)} onKeyDown={department.onKeyDown} onBlur={department.apply} 
            />
            <Select sm aria-label={L.brand} placeholder={L.brand} value={brand} options={BRAND_OPTIONS} onChange={(value) => set({ brand: value || null })}  />
            <Select sm aria-label={FILTERBAR_LABELS.sort} options={SORT_OPTIONS} value={sort.value} onChange={sort.onChange}  />
          </Stack>
        </SlideOver>
      </>
    );
  }
  return (
    <Flex gap={8} wrap>
      <div onKeyDown={search.onKeyDown} onBlur={search.apply}>
        <FilterBar
          label={NAV_ITEM_LABELS.requisitions}
          search={{ value: search.value, onChange: search.setValue, hs: "req.list.search" }}
          filters={chips}
          sort={sort}
        />
      </div>
      <StatusFilter params={params} set={set} view={view} openIn={openIn} setOpenIn={setOpenIn} />
      <Select sm aria-label={L.level} placeholder={L.level} value={level} options={LEVEL_OPTIONS} onChange={(value) => set({ level: value || null })}  />
      <Select
        sm aria-label={ROLE_LABELS.recruiter} placeholder={ROLE_LABELS.recruiter} value={owner}
        options={[{ value: "me", label: SURFACE_LABELS.ownReqs }]} onChange={(value) => set({ owner: value || null })} 
      />
      <Input
        sm aria-label={REQ_LABELS.department} placeholder={REQ_LABELS.department} value={department.value}
        onChange={(event) => department.setValue(event.currentTarget.value)} onKeyDown={department.onKeyDown} onBlur={department.apply} 
      />
      <Select sm aria-label={L.brand} placeholder={L.brand} value={brand} options={BRAND_OPTIONS} onChange={(value) => set({ brand: value || null })}  />
    </Flex>
  );
}

// ---- a row's words --------------------------------------------------------------------------------------------------

interface RowShared {
  columns: readonly RequisitionColumnDef[];
  cell: (row: FullRow, links?: boolean) => RequisitionCellContext;
  timeZone: string;
  now: number;
  /** The seat may open the assignment queue (`recruiter.queue`). */
  assign: boolean;
}

const levelWord = (row: FullRow): string => LEVEL_DISPLAY[row.level as Level] ?? row.level_label;
/** The status pill; none on a masked row, whose status is subject to permission (F-39). */
const statusPill = (row: FullRow) => (row.masked ? null : <Pill>{STATUS_WORD[row.status] ?? "—"}</Pill>);
const stationWord = (row: FullRow): string => MILESTONE_LABELS[stationOf(row.stage, row.status)];
const daysOpen = (row: FullRow, now: number): number => Math.max(0, Math.floor((now - toEpoch(row.opened_on)) / DAY));
const href = (row: FullRow): string | undefined => (row.can_open ? `${BASE}/${row.id}` : undefined);

/** Where a seat that may not open the requisition asks for access to it (V1's access page). */
function RequestAccess({ row, hs }: { row: FullRow; hs: string }) {
  return <TextLink href={`${BASE}/${row.id}/access?request=1`} hs={hs}>{ACCESS_LABELS.Request}</TextLink>;
}

/** The Recruiter, or his assignment word while none is assigned ("Recommended -> Pending TA Manager -> Assigned"), with
 * the way to the queue for a seat that may assign (F-36). */
function Recruiter({ row, shared, hs }: { row: FullRow; shared: RowShared; hs: string }) {
  if (row.owner_name) return <>{row.owner_name}</>;
  return (
    <>
      {RECRUITER_ASSIGNMENT_STATUS.pendingTaManager}
      {shared.assign ? <>{" "}<TextLink href={ASSIGNMENT_QUEUE} hs={hs}>{ACTION_LABELS.assign}</TextLink></> : null}
    </>
  );
}

/** The row's facts in the order the cards draw them; the slot columns sit where the table draws them. */
function facts(row: FullRow, index: number, shared: RowShared, links: boolean): KeyValueItem[] {
  return [
    [REQ_LABELS.phase, stationWord(row)],
    [ROLE_LABELS.recruiter, <Recruiter row={row} shared={shared}  />],
    [L.lastContact, formatDate(row.last_activity_at, shared.timeZone)],
    ...shared.columns.map((column): KeyValueItem => [column.header, column.cell(row, shared.cell(row, links))]),
    [REQ_LABELS.daysOpen, daysOpen(row, shared.now)],
    [SLA_LABELS.activeTime, row.phase.active_days ?? "—"],
    [SLA_LABELS.daysOnHold, row.phase.held_days],
  ];
}

// ---- the three views ------------------------------------------------------------------------------------------------

function ListTable({ rows, shared, loading, settled }: { rows: readonly FullRow[]; shared: RowShared; loading: boolean; settled: boolean }) {
  const cols = 10 + shared.columns.length;
  return (
    <Table
      dense
      label={NAV_ITEM_LABELS.requisitions}
      loading={loading ? 5 : undefined}
      empty={settled && rows.length === 0 ? "—" : undefined}
      cols={cols}
      head={(
        <Tr>
          <Th>{REQ_LABELS.requisition}</Th>
          <Th>{REQ_LABELS.title}</Th>
          <Th>{L.level}</Th>
          <Th>{L.status}</Th>
          <Th>{REQ_LABELS.phase}</Th>
          <Th>{ROLE_LABELS.recruiter}</Th>
          <Th>{L.lastContact}</Th>
          {shared.columns.map((column) => <Th key={column.key}>{column.header}</Th>)}
          <Th align="r">{REQ_LABELS.daysOpen}</Th>
          <Th align="r">{SLA_LABELS.activeTime}</Th>
          <Th align="r">{SLA_LABELS.daysOnHold}</Th>
        </Tr>
      )}
    >
      {rows.map((row, index) => <ListRow key={row.id} row={row} index={index} shared={shared} />)}
    </Table>
  );
}

function ListRow({ row, index, shared }: { row: FullRow; index: number; shared: RowShared }) {
  const open = href(row);
  return (
    <Tr >
      <Td label={REQ_LABELS.requisition} mono nowrap sub={row.req_no ?? undefined}>
        {open ? <TextLink href={open} >{row.id}</TextLink> : <>{row.id} <RequestAccess row={row}  /></>}
      </Td>
      <Td label={REQ_LABELS.title} sub={row.department}>{row.title}</Td>
      <Td label={L.level}>{levelWord(row)}</Td>
      <Td label={L.status}>{row.masked ? <span className="mc-t3">{MASKED_LABELS.subjectToPermission}</span> : statusPill(row)}</Td>
      <Td label={REQ_LABELS.phase}>{stationWord(row)}</Td>
      <Td label={ROLE_LABELS.recruiter}><Recruiter row={row} shared={shared}  /></Td>
      <Td label={L.lastContact} nowrap>{formatDate(row.last_activity_at, shared.timeZone)}</Td>
      {shared.columns.map((column) => <Td key={column.key} label={column.header}>{column.cell(row, shared.cell(row))}</Td>)}
      <Td label={REQ_LABELS.daysOpen} align="r">{daysOpen(row, shared.now)}</Td>
      <Td label={SLA_LABELS.activeTime} align="r">{row.phase.active_days ?? "—"}</Td>
      <Td label={SLA_LABELS.daysOnHold} align="r">{row.phase.held_days}</Td>
    </Tr>
  );
}

/** DC-53: one card per requisition, its health and readiness among its facts (never completion). */
function RequisitionCard({ row, index, shared, board }: { row: FullRow; index: number; shared: RowShared; board?: boolean }) {
  const open = href(row);
  const items = facts(row, index, shared, !open);
  return (
    <Card
      tight
      level={board ? 4 : 3}
      href={open}
      title={row.title}
      sub={<span className="mc-mono">{row.id}</span>}
      pills={statusPill(row)}
      
    >
      <Stack gap={8}>
        <KeyValue cols={2} items={board ? items.filter(([label]) => label !== REQ_LABELS.phase) : items} />
        {open ? null : <RequestAccess row={row}  />}
      </Stack>
    </Card>
  );
}

function ListCards({ rows, shared, loading }: { rows: readonly FullRow[]; shared: RowShared; loading: boolean }) {
  if (loading) return <Grid min={300} gap={12}>{[0, 1, 2].map((index) => <Card key={index} tight loading={{ lines: 5 }} />)}</Grid>;
  if (rows.length === 0) return <Card tight empty="—" />;
  return <Grid min={300} gap={12}>{rows.map((row, index) => <RequisitionCard key={row.id} row={row} index={index} shared={shared} />)}</Grid>;
}

function ListBoard({ rows, shared, loading }: { rows: readonly FullRow[]; shared: RowShared; loading: boolean }) {
  const columns = STATIONS.map((station) => ({
    title: MILESTONE_LABELS[station],
    hs: `req.list.board.${station}`,
    cards: rows
      .map((row, index) => ({ row, index }))
      .filter(({ row }) => stationOf(row.stage, row.status) === station)
      .map(({ row, index }) => <RequisitionCard key={row.id} row={row} index={index} shared={shared} board />),
  }));
  return <Kanban label={NAV_ITEM_LABELS.requisitions} columns={columns} scroll loading={loading || undefined} />;
}

/** Requisitions this seat holds only an interview assignment on (the panelist projection): his Home words for them. */
function AssignedElsewhere({ rows }: { rows: readonly PanelistListRow[] }) {
  return (
    <Stack gap={8}>
      <SectionTitle id="req-list-assigned">{WIDGET_LABELS.assignedInterviewerWorkElsewhere}</SectionTitle>
      <Table
        dense
        label={WIDGET_LABELS.assignedInterviewerWorkElsewhere}
        cols={3}
        head={(
          <Tr>
            <Th>{REQ_LABELS.requisition}</Th>
            <Th>{REQ_LABELS.title}</Th>
            <Th>{L.level}</Th>
          </Tr>
        )}
      >
        {rows.map((row, index) => (
          <Tr key={row.id} >
            <Td label={REQ_LABELS.requisition} mono nowrap sub={row.req_no ?? undefined}>
              <TextLink href={`${BASE}/${row.id}`} >{row.id}</TextLink>
            </Td>
            <Td label={REQ_LABELS.title} sub={row.department}>{row.title}</Td>
            <Td label={L.level}>{LEVEL_DISPLAY[row.level as Level] ?? row.level_label}</Td>
          </Tr>
        ))}
      </Table>
    </Stack>
  );
}
